import React, { useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import axios from 'axios'
import AlertDismissible from '../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import FormValidationSummary, { mergeValidationErrors, validationMessage } from '../../Shared/Components/FormValidationSummary'
import Layout from '../../Shared/Layout/LayoutAdmin'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Modal from 'react-bootstrap/Modal'
import { SIA_NAVY } from '../../constants/siaTheme'

const Reqd = () => <span className="text-danger">*</span>

function sliceDate(val) {
    if (!val) return ''
    return String(val).slice(0, 10)
}

function applyPolicyPrefill(current, prefill) {
    if (!prefill || typeof prefill !== 'object') {
        return current
    }

    const next = { ...current }

    const stringFields = [
        'insured_name',
        'rfc',
        'validity_date',
        'assignment_date',
        'biweekly_discount',
        'fi_available',
        'insured_amount',
    ]

    stringFields.forEach((field) => {
        if (prefill[field] !== undefined && prefill[field] !== null && prefill[field] !== '') {
            next[field] = String(prefill[field])
        }
    })

    if (prefill.office_id != null && prefill.office_id !== '') {
        next.office_id = String(prefill.office_id)
    }
    if (prefill.adviser_id != null && prefill.adviser_id !== '') {
        next.adviser_id = String(prefill.adviser_id)
    }

    return next
}

const SecomForm = ({ entry, offices, advisers, statuses, executive }) => {
    const { flash, errors: pageErrors } = usePage().props
    const isEdit = Boolean(entry)
    const [saving, setSaving] = useState(false)
    const [policyLoading, setPolicyLoading] = useState(false)
    const [policyError, setPolicyError] = useState('')
    const [cancelledPolicyModal, setCancelledPolicyModal] = useState(false)

    const form = useForm({
        office_id: entry?.office_id ? String(entry.office_id) : '',
        adviser_id: entry?.adviser_id ? String(entry.adviser_id) : '',
        secom_status_id: entry?.secom_status_id ? String(entry.secom_status_id) : '',
        insured_name: entry?.insured_name || '',
        rfc: entry?.rfc || '',
        policy_number: entry?.policy_number || '',
        validity_date: sliceDate(entry?.validity_date),
        biweekly_discount: entry?.biweekly_discount ?? '0',
        fi_available: entry?.fi_available ?? '0',
        insured_amount: entry?.insured_amount ?? '0',
        discount_metlife: entry?.discount_metlife ?? '0',
        discount_gnp: entry?.discount_gnp ?? '0',
        discount_magistral: entry?.discount_magistral ?? '0',
        assignment_date: sliceDate(entry?.assignment_date),
        action: entry?.action || '',
        results: entry?.results || '',
        adviser_results: entry?.adviser_results || '',
        ficha: null,
    })

    const allErrors = mergeValidationErrors(form.errors, pageErrors)
    const fieldError = (key) => validationMessage(allErrors, key)
    const invalidClass = (key) => (fieldError(key) ? 'is-invalid' : '')
    const FieldFeedback = ({ name }) =>
        fieldError(name) ? <div className="invalid-feedback d-block">{fieldError(name)}</div> : null

    const pathPrefix = 'admin/secom/registros'
    const fichaRequired = !isEdit || !entry?.ficha_path

    const buildPayload = () => ({
        ...form.data,
        biweekly_discount: Number(form.data.biweekly_discount) || 0,
        fi_available: Number(form.data.fi_available) || 0,
        insured_amount: Number(form.data.insured_amount) || 0,
        discount_metlife: Number(form.data.discount_metlife) || 0,
        discount_gnp: Number(form.data.discount_gnp) || 0,
        discount_magistral: Number(form.data.discount_magistral) || 0,
    })

    const lookupPolicy = async () => {
        const num = form.data.policy_number.trim()
        if (!num) {
            setPolicyError('Ingrese el número de póliza.')
            return
        }
        setPolicyLoading(true)
        setPolicyError('')
        try {
            const { data } = await axios.get(route('secom.lookup-policy'), { params: { policy_number: num } })
            if (!data.found) {
                if (data.cancelled) {
                    setCancelledPolicyModal(true)
                    setPolicyError('')
                } else {
                    setPolicyError(data.message || 'Póliza no encontrada.')
                }
                return
            }
            form.setData((current) => applyPolicyPrefill(current, data.prefill))
        } catch {
            setPolicyError('No se pudo consultar la póliza.')
        } finally {
            setPolicyLoading(false)
        }
    }

    const submit = (e) => {
        e.preventDefault()
        setSaving(true)
        const opts = {
            preserveScroll: true,
            forceFormData: true,
            onFinish: () => setSaving(false),
        }

        form.transform(() => buildPayload())

        if (isEdit) {
            form.put(route('secom.update', entry.id), opts)
        } else {
            form.post(route('secom.store'), opts)
        }
    }

    const textField = (label, field, type = 'text', rows = null) => (
        <div className="mb-4">
            <Form.Label className="fw-bold text-gray-800">
                {label}: <Reqd />
            </Form.Label>
            <Form.Control
                type={type}
                as={rows ? 'textarea' : undefined}
                rows={rows || undefined}
                value={form.data[field]}
                onChange={(e) => form.setData(field, e.target.value)}
                className={invalidClass(field)}
            />
            <FieldFeedback name={field} />
        </div>
    )

    const selectField = (label, field, options, labelKey = 'name', valueKey = 'id') => (
        <div className="mb-4">
            <Form.Label className="fw-bold text-gray-800">
                {label}: <Reqd />
            </Form.Label>
            <Form.Select
                value={form.data[field]}
                onChange={(e) => form.setData(field, e.target.value)}
                className={invalidClass(field)}
            >
                <option value="">Seleccione una opción</option>
                {(options || []).map((o) => (
                    <option key={o[valueKey]} value={o[valueKey]}>
                        {o[labelKey]}
                    </option>
                ))}
            </Form.Select>
            <FieldFeedback name={field} />
        </div>
    )

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            <FormValidationSummary errors={allErrors} />
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath={pathPrefix + (isEdit ? `/${entry.id}/edit` : '/create')} />
            </span>

            <div
                className="rounded-2 mb-6 py-4 px-4 text-center text-white text-uppercase fw-bold fs-5"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.04em' }}
            >
                Formulario de registro
            </div>

            <Card className="card-flush shadow-sm mb-10">
                <Card.Body className="py-8 px-6 px-lg-10">
                    <Form onSubmit={submit}>
                        <Row className="g-6 mb-2">
                            <Col md={12}>
                                <Form.Label className="fw-bold text-gray-600">Ejecutivo/a</Form.Label>
                                <div className="form-control form-control-solid bg-light fw-semibold text-gray-800">
                                    {executive?.display_name || '—'}
                                </div>
                                <div className="form-text">Se registra con el usuario con sesión iniciada.</div>
                            </Col>
                        </Row>

                        <Row className="g-6">
                            <Col lg={6}>
                                <div className="mb-4">
                                    <Form.Label className="fw-bold text-gray-800">
                                        Póliza: <Reqd />
                                    </Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            value={form.data.policy_number}
                                            onChange={(e) => form.setData('policy_number', e.target.value)}
                                            className={invalidClass('policy_number')}
                                            placeholder="Ingrese número de póliza"
                                        />
                                        <Button type="button" variant="secondary" onClick={lookupPolicy} disabled={policyLoading}>
                                            {policyLoading ? '…' : 'Buscar'}
                                        </Button>
                                    </div>
                                    {policyError && <div className="text-danger fs-8 mt-1">{policyError}</div>}
                                    <FieldFeedback name="policy_number" />
                                    <Form.Text className="text-muted">
                                        Busque la póliza para precargar datos concordantes (excepto acción, resultados y descuentos). Puede editarlos antes de guardar.
                                    </Form.Text>
                                </div>
                                {textField('Asegurado', 'insured_name')}
                                {textField('RFC', 'rfc')}
                                {textField('Vigencia', 'validity_date', 'date')}
                                {textField('Desc. Qnal.', 'biweekly_discount', 'number')}
                                {textField('FI disponible', 'fi_available', 'number')}
                                {selectField('Status', 'secom_status_id', statuses)}
                                {textField('Suma asegurada', 'insured_amount', 'number')}
                                {textField('Descuento Metlife', 'discount_metlife', 'number')}
                                {textField('Descuento GNP', 'discount_gnp', 'number')}
                                {textField('Descuento Magistral', 'discount_magistral', 'number')}
                            </Col>

                            <Col lg={6}>
                                {selectField('Oficina', 'office_id', offices, 'nombre', 'id')}
                                {selectField('Asesor asignado', 'adviser_id', advisers, 'display_name', 'id')}
                                {textField('Fecha de asignación', 'assignment_date', 'date')}
                                {textField('Acción', 'action', 'text', 4)}
                                {textField('Resultados', 'results', 'text', 4)}
                                {textField('Resultados del asesor', 'adviser_results', 'text', 4)}

                                <div className="mb-4">
                                    <Form.Label className="fw-bold text-gray-800">
                                        Cargar ficha {fichaRequired && <Reqd />}
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx"
                                        onChange={(e) => form.setData('ficha', e.target.files[0] || null)}
                                        className={invalidClass('ficha')}
                                    />
                                    {entry?.ficha_original_name && (
                                        <div className="text-muted fs-8 mt-2">
                                            Archivo actual: {entry.ficha_original_name}
                                        </div>
                                    )}
                                    <FieldFeedback name="ficha" />
                                </div>
                            </Col>
                        </Row>

                        <div className="d-flex flex-column align-items-center gap-4 mt-8">
                            <Button type="submit" variant="primary" size="lg" disabled={saving || form.processing}>
                                <i className="bi bi-save me-2" />
                                {saving ? 'Guardando…' : isEdit ? 'Actualizar registro' : 'Registrar'}
                            </Button>
                            <Link href={route('secom.index')} className="btn btn-sm btn-light">
                                <i className="bi bi-arrow-left me-1" />
                                Regresar al listado
                            </Link>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Modal show={cancelledPolicyModal} onHide={() => setCancelledPolicyModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Póliza cancelada</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    No se aceptan pólizas canceladas. Verifique el número de póliza e intente con otra vigente.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setCancelledPolicyModal(false)}>
                        Entendido
                    </Button>
                </Modal.Footer>
            </Modal>
        </Row>
    )
}

SecomForm.layout = (page) => <Layout title="SECOM — Registro">{page}</Layout>

export default SecomForm
